// Dependencies
import { Component } from 'react';
import { connect } from 'react-redux';

import { CHART_COLORS } from 'data/static';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Dispatch } from 'redux';
import { EnviroDisplayOrderType, EnviroDisplayType, PortType, PortURLType, RootState, TransitType } from 'types';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { getId } from 'utils/formHandlers';
import { translate } from 'utils/translate';

interface MetOceanChartComponentProps {
    data: any;
    transit: TransitType | null;
    dateTimeFormat: DateTimeFormat;
    time: DateTimeFormat | null;
    type: string | null;
    port: PortType;
}

interface MetOceanChartComponentState {
    charts: Highcharts.Options[];
    enviroDisplayOrder: EnviroDisplayOrderType[] | undefined;
    enviroDisplay: EnviroDisplayType | undefined;
    environmentalSources: PortURLType[] | undefined;
}

class MetOceanChart extends Component<MetOceanChartComponentProps, MetOceanChartComponentState> {
    constructor(props: MetOceanChartComponentProps) {
        super(props);
        this.state = {
            charts: [],
            enviroDisplayOrder: props.port.meta_data.enviroDisplayOrder,
            enviroDisplay: props.port.meta_data.enviroDisplay,
            environmentalSources: props.port.port_urls?.filter((x) => {
                return x.type === 'environmental';
            }),
        };
    }

    componentDidMount(): void {
        this.setState({
            charts: this.createChart(this.props.data),
        });
    }

    createChart = (chartsData): Highcharts.Options[] => {
        if (!this.props.transit || !this.state.environmentalSources) {
            return [];
        }
        const dateFormater = (date, long = false) => {
            return this.props.dateTimeFormat
                .parseUnixTime(date)
                .formatDateTimeToString(long ? 'dd LLL yyyy HH:mm:ss Z' : 'HH:mm');
        };

        // Handle finding the specific environment display order based on the selected type
        const enviroDisplayOrder = this.state.enviroDisplayOrder?.find(
            (enviroDisplayOrder) => enviroDisplayOrder.key === this.props.type
        );

        if (!enviroDisplayOrder || !this.state.enviroDisplay) {
            return []; // If no matching environment display order is found, return empty
        }

        // Loop through each item in the display order list
        return enviroDisplayOrder.list.map((listOrder: string): any => {
            // Get the title and unit based on the current display order

            const unit = this.enviroDisplay(this.props.type, listOrder, 'unit');
            const name = this.enviroDisplay(this.props.type, listOrder, 'name');
            const yAxisMax = this.enviroDisplay(this.props.type, listOrder, 'yAxisMax');
            const yAxisMin = this.enviroDisplay(this.props.type, listOrder, 'yAxisMin');
            const decimal = this.enviroDisplay(this.props.type, listOrder, 'decimal');

            return {
                key: getId(),
                accessibility: { enabled: false },
                chart: {
                    type: ['direction_from_deg', 'direction_to_deg'].includes(listOrder) ? 'scatter' : 'line',
                },
                credits: { enabled: false },
                title: {
                    text: name,
                    align: 'left',
                    x: 20,
                    style: {
                        fontSize: '16px',
                        fontWeight: 'normal',
                        textTransform: 'capitalize',
                    },
                },
                xAxis: {
                    type: 'datetime',
                    title: {
                        text: `Time UTC ${this.props.dateTimeFormat.parseDateTime().formatDateTimeToString('Z')}`,
                    },
                    labels: {
                        formatter: function () {
                            return dateFormater(this.value, false);
                        },
                    },

                    plotLines: this.props.time
                        ? [
                              {
                                  zIndex: 5,
                                  value: this.props.time.formatLocalDateToUTC().toJSDate(),
                                  color: 'red',
                                  width: 1,
                                  label: {
                                      text: translate('time'),
                                      align: 'right',
                                      verticalAlign: 'bottom',
                                      y: -5,
                                      style: {
                                          fontWeight: 'bold',
                                          color: 'red',
                                      },
                                  },
                              },
                          ]
                        : [],
                },
                yAxis: {
                    title: {
                        text: `${name} (${unit})`,
                    },
                    min: yAxisMin,
                    max: yAxisMax,
                },
                tooltip: {
                    shared: true,
                    useHTML: true,
                    formatter: function () {
                        const color = this.series.color;
                        const symbol = this.series.options.marker?.symbol || 'circle'; // default shape
                        // Map Highcharts marker shapes to CSS clip-path shapes
                        const shapeStyles = {
                            circle: 'border-radius: 50%;',
                            square: 'border-radius: 2px;',
                            diamond: 'transform: rotate(45deg); border-radius: 2px;',
                            triangle: `clip-path: polygon(50% 0%, 0% 100%, 100% 100%);`,
                            'triangle-down': `clip-path: polygon(0% 0%, 100% 0%, 50% 100%);`,
                        };

                        // fallback if shape not mapped
                        const shapeStyle = shapeStyles[symbol] || 'border-radius: 50%;';

                        return `
                                <strong>${translate('date')}:</strong> 
                                ${dateFormater(new Date(this.x).getTime(), true)}<br>

                                <div style="display:flex; align-items:center; margin-top:6px;">
                                    <span style="
                                        display:inline-block;
                                        width:12px;
                                        height:12px;
                                        background:${color};
                                        margin-right:6px;
                                        ${shapeStyle}
                                    "></span>

                                    <strong>${this.series.name}:</strong>
                                    ${parseFloat(this.y.toFixed(decimal))}${unit}
                                </div>
                            `;
                    },

                    series: chartsData.map((chartDate, index) => {
                        return {
                            name: `${chartDate.url.description} - ${this.enviroDisplay(
                                this.props.type,
                                listOrder,
                                'name'
                            )}`,
                            type: ['direction_from_deg', 'direction_to_deg'].includes(listOrder) ? 'scatter' : 'line',
                            data: chartDate.data
                                .filter((record) => {
                                    return this.props.dateTimeFormat
                                        .parseDateTime(record.valid_time_utc)
                                        .isBetween(
                                            this.props.transit?.start_utc.date,
                                            this.props.transit?.end_utc.date
                                        );
                                })
                                .map((record) => {
                                    return {
                                        x: this.props.dateTimeFormat
                                            .parseDateTime(record.valid_time_utc)
                                            .formatUTCDateToLocal()
                                            .toJSDate(),
                                        y:
                                            record[listOrder] *
                                            this.enviroDisplay(
                                                this.props.type,
                                                listOrder,
                                                'database2display_unit_factor'
                                            ),
                                    };
                                }),

                            marker: {
                                radius: 3,
                                symbol: 'circle',
                            },

                            color: CHART_COLORS[index],
                        };
                    }),
                },
                series: chartsData.map((chartDate, index) => {
                    return {
                        name: `${chartDate.url.description} - ${this.enviroDisplay(
                            this.props.type,
                            listOrder,
                            'name'
                        )}`,
                        type: ['direction_from_deg', 'direction_to_deg'].includes(listOrder) ? 'scatter' : 'line',

                        data: chartDate.data
                            .filter((record) => {
                                return this.props.dateTimeFormat
                                    .parseDateTime(record.valid_time_utc)
                                    .isBetween(this.props.transit?.start_utc.date, this.props.transit?.end_utc.date);
                            })
                            .map((record) => {
                                return {
                                    x: this.props.dateTimeFormat.parseDateTime(record.valid_time_utc).toJSDate(),
                                    y:
                                        record[listOrder] *
                                        this.enviroDisplay(this.props.type, listOrder, 'database2display_unit_factor'),
                                };
                            }),

                        marker: {
                            radius: 3,
                            symbol: 'circle',
                        },

                        color: CHART_COLORS[index],
                    };
                }),
            };
        });
    };

    enviroDisplay = (type, list, key) => {
        if (!this.state.enviroDisplay) {
            return {};
        }
        return this.state.enviroDisplay[type][list][key];
    };

    render() {
        return (
            <div>
                {this.state.charts?.map((chart, i) => {
                    return (
                        <div key={i}>
                            <HighchartsReact highcharts={Highcharts} options={chart} />
                        </div>
                    );
                })}
            </div>
        );
    }
}

const MetOceanChartStateToProps = (state: RootState) => ({
    dateTimeFormat: state.port.dateTimeFormat,
    port: state.port.port,
});

const MetOceanChartDispatchToProps = (dispatch: Dispatch) => ({});

export default connect(MetOceanChartStateToProps, MetOceanChartDispatchToProps)(MetOceanChart);
